import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { browse, cached, cachedHit, readFilters, remember, requestBody, seedAdded, writeFilters, type BrowseRes, type Card, type Filters } from './browse'
import { cachedAt, STALE_EVENT } from './browseCache'
import { onOwnerChange } from './owner'
import { errorMessage } from './format'
import { ApiError } from './api'
import { setPlanZone } from './plan'
import { cachedProfile, firstRunDoneHere } from './v2'
import { useTaste, withoutHidden } from './tasteFeedback'
import { mergeQuiet } from './browseMerge'
import { setWords, useWords } from './words'
import { useFlag } from './flags'

/** fr3 fix (QA prod r1): the saved home this view centers on, when the filters name no place and no plan; null otherwise.
 *  The server knows the home from the session, so the request only carries the distance (lib/browse requestBody). */
export function homeArea(f: Filters): string | null {
  if (f.plan || (f.lat !== null && f.lng !== null) || f.where.trim()) return null
  const home = firstRunDoneHere() ? cachedProfile()?.home ?? null : null
  return home?.label && home.lat != null && home.lng != null ? home.label : null
}

/** Filters from the URL; changing them rewrites the query (replace, so Back leaves the page instead of undoing a filter). */
export function useFilters(): [Filters, (p: Partial<Filters>) => void] {
  const [q] = useSearchParams()
  const navigate = useNavigate()
  const loc = useLocation()
  const words = useWords()
  const f = useMemo(() => ({ ...readFilters(q), ...words }), [q, words])
  const set = useCallback(
    (p: Partial<Filters>) => {
      // R-94: the words live on the device, not in the URL
      if (p.inc || p.exc) setWords({ inc: p.inc ?? f.inc, exc: p.exc ?? f.exc })
      const next = writeFilters({ ...f, ...p }, new URLSearchParams(loc.search))
      next.delete('item')
      navigate({ pathname: loc.pathname, search: next.toString() }, { replace: true, preventScrollReset: true })
    },
    [f, loc.pathname, loc.search, navigate],
  )
  return [f, set]
}

/** The query string that carries only the filters (for links to category pages and back to Browse). */
export const filterSearch = (f: Filters) => {
  const s = writeFilters(f).toString()
  return s ? `?${s}` : ''
}

/** placeFresh (R10): the answer on screen was for the place now in the filters (where / Use my location), so its
 *  `center.label` ("Playa Vista 90094") names that place; false while a new place is still loading. */
/** stale (QA prod r1, B-069): the answer on screen was for other filters and the new one is loading; screens show the
 *  loading state instead of a list that does not match what the filters say. */
type Data = { res: BrowseRes | null; stale: boolean; placeFresh: boolean; error: string | null; errorStatus: number | null; loading: boolean; slow: boolean; more: boolean; loadMore: () => void; retry: () => void; refresh: () => void }

/**
 * One browse view (the front page, a category, a row's "see all"): fetches on every filter change (250 ms debounce),
 * keeps the last answer on screen while the next loads, keeps pages already loaded, and caches per view in the session
 * so going back is instant.
 */
export function useBrowse(f: Filters, extra: { cat?: string; row?: string; collection?: string } = {}): Data {
  const home = homeArea(f) !== null
  // a QA switch of either flag asks again (requestBody reads them)
  const flags = `${useFlag('words_filter')}${useFlag('fame_filter')}`
  const body = useMemo(() => requestBody(f, extra, home), [f, extra.cat, extra.row, extra.collection, home, flags]) // eslint-disable-line react-hooks/exhaustive-deps
  const key = JSON.stringify(body)
  const [res, setRes] = useState<BrowseRes | null>(() => cached(key))
  const place = JSON.stringify([f.where.trim(), f.lat, f.lng])
  const [resPlace, setResPlace] = useState<string | null>(() => (cached(key) ? place : null))
  const [resKey, setResKey] = useState<string | null>(() => (cached(key) ? key : null))
  const [error, setError] = useState<string | null>(null)
  const [errorStatus, setErrorStatus] = useState<number | null>(null)
  const [loading, setLoading] = useState(!res)
  const [slow, setSlow] = useState(false)
  const [more, setMore] = useState(false)
  const [round, setRound] = useState(0)
  const seq = useRef(0)
  const quiet = useRef(false) // refresh(): ask again without dimming the page or showing errors (fr4)
  const resRef = useRef(res)
  resRef.current = res
  const resKeyRef = useRef(resKey)
  resKeyRef.current = resKey

  useEffect(() => {
    const got = cachedHit(key)
    const hit = got?.res ?? null
    if (hit) {
      setRes(hit)
      setResPlace(place)
      setResKey(key)
      setLoading(false)
      setError(null)
      seedAdded([...hit.items, ...hit.rows.flatMap((r) => r.items)])
      setPlanZone(hit.city?.tz, hit.city?.label)
      // gate 10/16: a fresh cached answer still outranks any call in flight for an earlier key (Hide this kind then Undo:
      // the exclude call landed after Undo and left the list empty, stale for good)
      if (round === 0 && got?.fresh) { seq.current++; return }
      // B-163: a stale answer (older than the time limit, or from before a Save / More like this / calendar add /
      // check-in) stays on screen and the server is asked again quietly; the list changes once, when the answer lands
      if (round === 0) quiet.current = true
    }
    const first = seq.current === 0 // F5: a view's first load has nothing to debounce
    const mine = ++seq.current
    const hush = quiet.current
    quiet.current = false
    if (!hush) {
      setLoading(true)
      setError(null)
      setErrorStatus(null)
    }
    const slowTimer = hush ? 0 : window.setTimeout(() => setSlow(true), 4000)
    const t = window.setTimeout(async () => {
      try {
        const got = await browse(body, key)
        if (mine !== seq.current) return
        setPlanZone(got.city?.tz, got.city?.label)
        seedAdded([...got.items, ...got.rows.flatMap((r) => r.items)])
        // a quiet answer for the list already on screen never moves a card under a finger (lib/browseMerge.ts)
        const cur = resRef.current
        setRes(hush && cur && resKeyRef.current === key ? mergeQuiet(cur, got) : got)
        setResPlace(place)
        setResKey(key)
      } catch (e) {
        if (mine === seq.current && !hush) {
          setError(errorMessage(e))
          setErrorStatus(e instanceof ApiError ? e.status : null)
        }
      } finally {
        if (mine === seq.current) {
          setLoading(false)
          setSlow(false)
        }
        window.clearTimeout(slowTimer)
      }
    }, hit || first ? 0 : 250)
    return () => {
      window.clearTimeout(t)
      window.clearTimeout(slowTimer)
    }
  }, [key, round]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = useCallback(async () => {
    const cur = resRef.current
    if (!cur || cur.next_offset === null || more) return
    setMore(true)
    try {
      const got = await browse({ ...body, offset: cur.next_offset })
      const seen = new Set(cur.items.map((c: Card) => c.item.id))
      const merged: BrowseRes = { ...cur, items: [...cur.items, ...got.items.filter((c) => !seen.has(c.item.id))], next_offset: got.next_offset }
      seedAdded(got.items)
      setRes(merged)
      remember(key, merged, cachedAt(key))
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setMore(false)
    }
  }, [body, key, more])

  // B-161: another person in this tab (Delete my data or sign-out in another tab): the old person's answer leaves the
  // screen at once and this view asks again as the new one
  useEffect(() => onOwnerChange(() => {
    setRes(null)
    setResKey(null)
    setResPlace(null)
    setRound((r) => r + 1)
  }), [])
  // B-163: a taste signal while this view stays on screen (a Save in the detail sheet, a calendar add in another tab) never
  // reorders the list under a finger; the view asks again when the person comes back to the tab
  useEffect(() => {
    let stale = false
    const onStale = () => { stale = true }
    const onShow = () => {
      if (document.visibilityState !== 'visible') return
      const hit = cachedHit(key)
      if (!stale && hit?.fresh) return
      stale = false
      quiet.current = true
      setRound((r) => r + 1)
    }
    window.addEventListener(STALE_EVENT, onStale)
    document.addEventListener('visibilitychange', onShow)
    return () => {
      window.removeEventListener(STALE_EVENT, onStale)
      document.removeEventListener('visibilitychange', onShow)
    }
  }, [key])

  // R-14: what they said is not for them leaves every list at once, cached answers included
  const { hidden } = useTaste()
  const shown = useMemo(() => withoutHidden(res, hidden), [res, hidden])
  return { res: shown, stale: res !== null && resKey !== key, placeFresh: resPlace === place, error, errorStatus, loading, slow, more, loadMore, retry: () => setRound((r) => r + 1), refresh: () => { quiet.current = true; setRound((r) => r + 1) } }
}

/** Keep and restore this page's scroll position across Back (the list is already on screen from the session cache). */
export function useKeepScroll(ready: boolean) {
  const loc = useLocation()
  const id = `mg_scroll:${loc.pathname}${loc.search.replace(/[?&]item=[^&]*/, '')}`
  const restored = useRef(false)
  useEffect(() => {
    if (!ready || restored.current) return
    restored.current = true
    const y = Number(sessionStorage.getItem(id) ?? 0)
    if (y > 0) requestAnimationFrame(() => window.scrollTo(0, y))
  }, [ready, id])
  useEffect(() => {
    let raf = 0
    const save = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        try {
          sessionStorage.setItem(id, String(Math.round(window.scrollY)))
        } catch { /* convenience only */ }
      })
    }
    window.addEventListener('scroll', save, { passive: true })
    return () => window.removeEventListener('scroll', save)
  }, [id])
}

/** B-069: when the filters change under a scrolled list, bring the top of the results back into view, so the loading
 *  row that replaces the old list is what they see (not the page footer of a list that just collapsed). */
export function useScrollToResults(stale: boolean, anchor: { current: HTMLElement | null }) {
  useEffect(() => {
    if (!stale || !anchor.current) return
    const top = anchor.current.getBoundingClientRect().top + window.scrollY - 140 // under the sticky nav and summary
    if (window.scrollY > top) window.scrollTo({ top: Math.max(0, top) })
  }, [stale]) // eslint-disable-line react-hooks/exhaustive-deps
}

/** True once an element has scrolled up out of view (drives the compact filter summary). */
export function useScrolledPast<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [past, setPast] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(([e]) => setPast(!e.isIntersecting && e.boundingClientRect.top < 0), { rootMargin: '-72px 0px 0px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [])
  return [ref, past] as const
}
